import React, { useState, useEffect, useMemo } from 'react'
import moment from 'moment'
import { useTheme } from '../../../../context/ThemeProvider'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faXmarkCircle,
  faPlusCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../../hooks/useAuth'
import {toast} from 'sonner'

const CreateStaffSlideOut = ({ open, setOpen, mode = 'create', staff = null, staffGroups = [], onStaffAddedOrUpdated, onStaffDeleted, isArchivedContext = false, onArchivedStaffRemoved }) => {
  const theme = useTheme()
  const axios = useAxiosPrivate()
  const axiosSai = useAxiosPrivateSAI()
  const [policyData, setPolicyData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }

  const { setShowLoader } = useAuth()
  const [value, setValue] = useState({
    dob: moment().subtract(18, 'years').format('YYYY-MM-DD'),
    staffType: '',
  })

  const [fieldChangeFlags, setFieldChangeFlags] = useState({
    hasForenameChanged: false,
    hasSurnameChanged: false,
    hasDobChanged: false,
    hasFteChanged: false,
    hasStaffTypeChanged: false,
    hasInceptionChanged: false,
    hasExpiryChanged: false,
  })

  // --- helpers ---
  const normaliseString = (value) => (value ?? '').toString().trim()
  const normaliseDate = (value) =>
    value ? moment(value, ['YYYY-MM-DD','DD/MM/YYYY'], true).format('YYYY-MM-DD') : ''
  const formatDateDisplay = (value) =>
    value && moment(value, ['YYYY-MM-DD','DD/MM/YYYY'], true).isValid()
      ? moment(value).format('DD/MM/YYYY')
      : ''
    
  const previousFromServer = React.useMemo(() => {
    try { return staff?.changes ? JSON.parse(staff.changes) : null; } catch { return null; }
  }, [staff?.changes])
  
  const oldValues = React.useMemo(() => ({
    dob:      staff?.dob ?? '',
    fte:      previousFromServer?.fte ?? staff?.fte ?? '',
    staffType:previousFromServer?.staff_type ?? staff?.staff_type ?? '',
    inception:previousFromServer?.inception_date ?? staff?.inception ?? '',
    expiry:   previousFromServer?.end_date ?? staff?.expiry ?? '',
  }), [staff, previousFromServer])
  
  useEffect(() => {
    if (mode === 'edit') {
      setFieldChangeFlags({
        hasDobChanged:       normaliseDate(value.dob)        !== normaliseDate(oldValues.dob),
        hasFteChanged:       normaliseString(value.fte)      !== normaliseString(oldValues.fte),
        hasStaffTypeChanged: normaliseString(value.staffType)!== normaliseString(oldValues.staffType),
        hasInceptionChanged: normaliseDate(value.inception)  !== normaliseDate(oldValues.inception),
        hasExpiryChanged:    normaliseDate(value.expiry)     !== normaliseDate(oldValues.expiry),
      });
    } else {
      setFieldChangeFlags({
        hasDobChanged: false,
        hasFteChanged: false,
        hasStaffTypeChanged: false,
        hasInceptionChanged: false,
        hasExpiryChanged: false,
      });
    }
  }, [mode, value, oldValues]);

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    const storedPolicy = JSON.parse(localStorage.getItem('policyData'))
    setPolicyData(storedPolicy)
  }, [])

  const postPolicyStaffEndDate = async (expiry) => {
    if (!staff?.policy_staff_id || !staff?.policy_id || !staff?.staff_type) {
      toast.error('Missing policy/staff identifiers for date update');
      return false
    }
  
    const params = {
      policy_staff_id: staff.policy_staff_id,
      policy_id: staff.policy_id,
      staff_type: staff.staff_type,
      date: expiry,
    };
  
    try {
      const { data } = await axiosSai.post('/api/policy_staffs_dates', params)
  
      if (data?.error) {
        toast.error(data.message || 'Failed to update expiry date')
        setValue(prev => ({ ...prev, expiry: staff.expiry || prev.expiry }))
        return false
      }

      onStaffAddedOrUpdated?.()
      return true;
    } catch (err) {
      console.error('Error updating staff end date', err)
      toast.error('Error updating staff end date')
      return false
    }
  }  

  const deleteStaff = async () => {
    try {
      setIsLoading(true)
      const created = moment().format('YYYY-MM-DD')
      const updObj = {
        end_date: created,
        policy_staff_status: 'draft',
        active: 0,
        isSubmitted: 0
      }
  
      await axiosSai.post(`/api/policy_staffs/expire/${staff.policy_staff_id}`, updObj)
      await postPolicyStaffEndDate(created)
      toast.success('Staff member marked as deleted')

      onStaffDeleted(staff.id)
      setOpen(false)
    } catch (error) {
      console.error('Expire error:', error)
      toast.error('Failed to mark staff member as deleted')
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && staff) {
      setValue({
        forename: staff.forename || '',
        surname: staff.surname || '',
        dob: staff.dob || '',
        fte: staff.fte || '',
        inception: staff.inception || '',
        expiry: staff.expiry || '',
        staffType: staff.staff_type?.trim() || '',
        policy_staff_id: staff.policy_staff_id,
        policy_id: staff.policy_id,
        id: staff.staff_id,
      });
    } else {
      setValue({
        forename: '',
        surname: '',
        dob: moment().subtract(18, 'years').format('YYYY-MM-DD'),
        fte: '',
        inception: moment().format('YYYY-MM-DD'),
        expiry: moment().format('YYYY-MM-DD'),
        staffType: '',
      })
    }
  }, [open, mode, staff])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setShowLoader(true)
  
    try {
      const org = JSON.parse(localStorage.getItem('orgType'));
      if (!org?.type || !org?.id) {
        toast.error('Organisation data missing');
        return;
      }
      const norm = (string) => (string ?? '').toString().trim();
      const same = (name, name2) => norm(name) === norm(name2);
      const surnameChanged = !same(value.surname, staff?.surname)
      const forenameChanged = !same(value.forename, staff?.forename)
      const fteChanged = !same(value.fte, staff?.fte);
      const staffTypeChanged = !same(value.staffType, staff?.staff_type)
      const expiryChanged = value.expiry
        ? !same(
            moment(value.expiry).format('YYYY-MM-DD'),
            moment(staff?.expiry).format('YYYY-MM-DD')
          )
        : false;

      if (mode === 'create') {
        const formattedDOB = moment(value.dob).format('YYYY-MM-DD');
        const payload = {
          ...value,
          date_of_birth: formattedDOB,
          pid: staff?.policy_id,
          type: value.staffType,
        };
  
        const response = await axiosSai.post(
          `/api/staffs/ajax_add/${org.type}/${org.id}`,
          payload
        );
  
        if (response.data === 0) {
          toast.error('This staff member already exists');
        } else {
          toast.success('Staff member added successfully');
          setOpen(false);
          window.location.reload();
        }
        return;
      }
      if ((surnameChanged || forenameChanged) && staff?.policy_staff_id) {
        const payload = {
          forename: norm(value.forename ?? staff.forename),
          surname:  norm(value.surname  ?? staff.surname),
        };
  
        await axiosSai.put(`/api/staffs/update_ajax/${staff?.id}`, payload);

        await axiosSai.put(`/api/policy_staffs/${staff.policy_staff_id}`, {
          policy_staff_status: 'draft',
          active: staff.active ?? 1,
          isSubmitted: 0,
        });

        toast.success('Staff member name updated');
        await onStaffAddedOrUpdated?.();
        setOpen(false);
        return;
      }
  
      // EDIT: else if FTE / Expire / Staff Type updated, hit the POLICY_STAFFS route
      if ((fteChanged || expiryChanged || staffTypeChanged) && staff?.policy_staff_id) {
        const payload = {
          policy_staff_id: staff.policy_staff_id,
          policy_id: policyData?.policy_id ?? staff?.policy_id,
          ...(fteChanged ? { fte: value.fte } : {}),
          ...(staffTypeChanged ? { staff_type: value.staffType } : {}),
          ...(expiryChanged
            ? { end_date: moment(value.expiry).format('YYYY-MM-DD') }
            : {}),
          policy_staff_status: 'draft',
          active: 0,
          isSubmitted: 0,
        }
  
        await axiosSai.put(`/api/policy_staffs/${staff.policy_staff_id}`, payload)
        toast.success('Staff member updated')
        await onStaffAddedOrUpdated?.()
        setOpen(false)
        return
      }
  
      if (isArchivedContext === true && staff?.policy_staff_id && staff?.policy_id) {
        const payload = {
          policy_staff_id: staff.policy_staff_id,
          policy_id: staff.policy_id,
          fte: value.fte,
          policy_staff_status: 'draft',
          active: 0,
          isSubmitted: 0,
          inception_date: moment().format('YYYY-MM-DD'),
          staff_id: staff.id,
        }
        await axiosSai.put(`/api/staffs/update_ajax/${staff?.policy_staff_id}`, payload)
        toast.success('Archived staff member updated')
        await onStaffAddedOrUpdated?.()
        setOpen(false)
        return
      }
  
      toast.message('No changes to save')
    } catch (error) {
      console.error('Error submitting staff form:', error)
      toast.error('An error occurred')
    } finally {
      setShowLoader(false)
    }
  }

  const revertStaffMemberChanges = async () => {
    const staffId = staff?.id
    if (!staffId) {
      toast.error('Missing staff ID')
      return
    }
  
    try {
      setShowLoader(true)
      await axiosSai.post(`/api/policy_staffs/revert/${staffId}`)
      toast.success('Changes reverted successfully')
      await onStaffAddedOrUpdated?.()
      setOpen(false)
    } catch (error) {
      console.error('Error reverting changes:', error)
      toast.error('Failed to revert changes')
    } finally {
      setShowLoader(false)
    }
  }
  
  const isFormValid = value.forename?.trim() &&
  value.surname?.trim() &&
  value.dob &&
  value.fte &&
  value.staffType &&
  (mode !== 'edit' || (value.inception && value.expiry));

  const addStaffToPolicy = async () => {
    try {
      setShowLoader(true)
  
      const policyId  = policyData?.policy_id
      const staffType = (staff?.staff_type || '').trim()
      const staffId   = staff?.id
  
      if (!policyId || !staffType || !staffId) {
        toast.error('Missing policy, staff type, or staff id')
        return
      }
  
      const payload = {
        policy_id: policyId,
        staff_type: staffType,
        staff_id: staffId,
      }
  
      await axiosSai.post(`/api/policy_staffs/${policyId}`, payload);
  
      onArchivedStaffRemoved?.(staffId, staffType)
      await onStaffAddedOrUpdated?.()
  
      toast.success('Staff added to policy')
      setOpen(false)
    } catch (err) {
      if (err?.response?.status === 409 || err?.response?.data?.message?.toLowerCase?.().includes('duplicate')) {
        toast.error('Duplicate: this staff member is already in that category.')
      } else {
        console.error('addStaffToPolicy error:', err)
        toast.error('Failed to add staff to policy')
      }
    } finally {
      setShowLoader(false)
    }
  }

  const clearFields = () => {
    setValue({})
    setFieldChangeFlags({
      hasForenameChanged: false,
      hasSurnameChanged: false,
      hasDobChanged: false,
      hasFteChanged: false,
      hasStaffTypeChanged: false,
      hasInceptionChanged: false,
      hasExpiryChanged: false,
    })
    // setTermsAccepted(false)
  }

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-[100]">
       <>
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-800">
                Revert all unsubmitted staff changes?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                This will revert the changes you have made to all <strong>unsubmitted</strong> staff members. This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                  onClick={revertStaffMemberChanges}
                >
                  Yes, revert
                </button>
              </div>
            </div>
          </div>
        )}
      </>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition duration-500 ease-in-out data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div className=" px-4 py-6 sm:px-6"  style={{ backgroundColor: style.backgroundColor }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="text-lg font-semibold leading-6 text-white">
                          {mode === 'edit' ? `Edit details for ${staff?.forename + ' ' + staff?.surname || ''}` : 'Create a new Staff Member'}
                      </DialogTitle>
                      <div className="text-xs text-white">
                        Please complete the required information below to submit
                        your request.
                      </div>
                    </div>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="relative rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={() => {
                          clearFields()
                          setOpen(false)
                        }}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <FontAwesomeIcon  className="h-6 w-6" icon={faXmarkCircle} />
                      </button>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-y-auto relative" >
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 p-6'>
                    <div>
                      <label className="block mb-1 font-medium text-sm text-gray-700">Forename <span className='text-red-500'>*</span></label>
                      <input
                        type="text"
                        value={value.forename || ''}
                        onChange={(e) => setValue(prev => ({ ...prev, forename: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        disabled={mode === 'edit' ? true : false}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium text-sm text-gray-700">Surname <span className='text-red-500'>*</span></label>
                      <input
                        type="text"
                        value={value.surname || ''}
                        onChange={(e) => setValue(prev => ({ ...prev, surname: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        disabled={isArchivedContext ? true : false}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium text-sm text-gray-700">Date of Birth <span className='text-red-500'>*</span></label>
                      <input
                        type="date"
                        value={value.dob}
                        onChange={(e) => setValue(prev => ({ ...prev, dob: e.target.value }))}
                        disabled={mode === 'edit'}
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${mode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      />
                      {fieldChangeFlags.hasDobChanged && (
                        <p className="text-xs text-gray-500 italic mt-1">(was: {formatDateDisplay(oldValues.dob) || '—'})</p>
                      )}
                    </div>
                      
                    <div>
                      <label className="block mb-1 font-medium text-sm text-gray-700">FTE <span className='text-red-500'>*</span></label>
                      <input
                        type="text"
                        value={value.fte || ''}
                        onChange={(e) => setValue(prev => ({ ...prev, fte: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        disabled={isArchivedContext ? true : false}
                      />
                      {fieldChangeFlags.hasFteChanged && (
                        <p className="text-xs text-gray-500 italic mt-1">(was: {oldValues.fte || '—'})</p>
                      )}
                    </div>
                    {mode === 'edit' && (
                      <div>
                        <label className="block mb-1 font-medium text-sm text-gray-700">Inception <span className='text-red-500'>*</span></label>
                        <input
                          type="date"
                          value={value.inception}
                          onChange={(e) => setValue(prev => ({ ...prev, inception: e.target.value }))}
                          disabled={mode === 'edit'}
                          className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${mode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        />
                        {fieldChangeFlags.hasInceptionChanged && (
                          <p className="text-xs text-gray-500 italic mt-1">(was: {formatDateDisplay(oldValues.inception) || '—'})</p>
                        )}
                      </div>
                    )}
                    {mode === 'edit' && (
                      <div>
                        <label className="flex items justify-between mb-1 font-medium text-sm text-gray-700"><div>Expire <span className='text-red-500'>*</span> </div>
                          {moment(value.expiry, ['YYYY-MM-DD', 'DD/MM/YYYY'], true).isSameOrAfter(moment(), 'day') && (
                            <>
                              {isLoading ? (
                                <FontAwesomeIcon icon={faSpinner} spin size="2xs" />
                              ) : (
                                <div
                                  onClick={() => deleteStaff(staff)}
                                  className='text-red-500 cursor-pointer hover:underline text-xs'
                                >
                                  Expire Today
                                </div>
                              )}
                            </>
                          )}
                        </label>
                        <input
                          type="date"
                          value={value.expiry}
                          onChange={(e) => setValue(prev => ({ ...prev, expiry: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          disabled={isArchivedContext ? true : false}
                        />
                        {fieldChangeFlags.hasExpiryChanged && (
                          <p className="text-xs text-gray-500 italic mt-1">
                            (was: {formatDateDisplay(oldValues.expiry) || '—'})
                          </p>
                        )}
                      </div>
                    )}
                    <div className='col-span-2'>
                      <label className="block mb-1 font-medium text-sm text-gray-700">Staff Type <span className='text-red-500'>*</span></label>
                        <select
                          value={value.staffType}
                          disabled={isArchivedContext ? true : false}
                          onChange={(e) => setValue(prev => ({ ...prev, staffType: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Select Staff Type</option>
                          {staffGroups.map((group, idx) => (
                            <option key={idx} value={group}>
                              {group}
                            </option>
                          ))}
                      </select>
                      {fieldChangeFlags.hasStaffTypeChanged && (
                        <p className="text-xs text-gray-500 italic mt-1">(was: {oldValues.staffType || '—'})</p>
                      )}
                    </div>
                    <div className="border-t w-11/12 mx-auto py-4 absolute bottom-0 flex justify-between items-center space-x-2">
                      {!isArchivedContext && (
                        <button
                          type="submit"
                          className={`button text-sm ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={{ borderColor: style.color }}
                          disabled={!isFormValid}
                        >
                          <span className="" style={{ color: style.color }}>
                            {mode === 'edit' ? `Save Changes` : 'Add Staff'} 
                          </span>
                          <span
                            className=""
                            style={{ backgroundColor: style.backgroundColor }}
                          >
                            <FontAwesomeIcon icon={faPlusCircle} />
                          </span>
                        </button>
                      )}
                      {mode === 'edit' && staff?.policy_staff_status === 'draft' && (
                        <button
                          type="button"
                          onClick={() => setShowConfirmModal(true)}
                          className="button border-amber-600"
                          title="Revert changes for all unsubmitted staff members"
                        >
                          <span className="text-amber-600">Revert Changes</span>
                          <span className="bg-amber-600">
                            <FontAwesomeIcon icon={faXmarkCircle} />
                          </span>
                        </button>
                      )}
                      {isArchivedContext && (
                        <button
                          type="button"
                          onClick={() => addStaffToPolicy(staff)}
                          className={`button ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={{ borderColor: style.color }}
                          disabled={!isFormValid}
                        >
                          <span style={{ color: style.color }}>
                            Add Staff onto Policy 
                          </span>
                          <span style={{ backgroundColor: style.backgroundColor }}>
                            <FontAwesomeIcon icon={faPlusCircle} />
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default CreateStaffSlideOut
